import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiUser, FiPhone, FiMail, FiMapPin, FiDollarSign, FiChevronDown, FiChevronUp } = FiIcons;

const OrderCard = ({ order, onStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'preparing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ready': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-warm-brown/10 text-warm-brown border-warm-brown/20';
    }
  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      case 'pending': return 'preparing';
      case 'preparing': return 'ready';
      case 'ready': return 'completed';
      default: return null;
    }
  };

  const getStatusButtonText = (currentStatus) => {
    switch (currentStatus) {
      case 'pending': return 'Start Preparing';
      case 'preparing': return 'Mark Ready';
      case 'ready': return 'Mark Completed';
      default: return null;
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (dateString) => {
    const now = new Date();
    const orderTime = new Date(dateString);
    const diffInMinutes = Math.floor((now - orderTime) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    }
  };

  return (
    <motion.div
      layout
      className="bg-white rounded-xl shadow-lg border border-warm-brown/10 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-warm-brown/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-playfair text-warm-brown font-medium">
              Order #{order.id}
            </h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-warm-brown flex items-center gap-1">
              <SafeIcon icon={FiDollarSign} className="text-lg" />
              {order.total.toFixed(2)}
            </p>
            <p className="text-sm text-warm-brown/70 flex items-center gap-1">
              <SafeIcon icon={FiClock} className="text-xs" />
              {formatTime(order.createdAt)} ({getTimeSince(order.createdAt)})
            </p>
          </div>
        </div>

        {/* Customer Info */}
        <div className="grid md:grid-cols-3 gap-4 text-sm text-warm-brown/80">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiUser} className="text-warm-brown/50" />
            <span>{order.customerInfo.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiPhone} className="text-warm-brown/50" />
            <span>{order.customerInfo.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiMail} className="text-warm-brown/50" />
            <span>{order.customerInfo.email}</span>
          </div>
        </div>

        {order.customerInfo.address && (
          <div className="mt-2 flex items-start gap-2 text-sm text-warm-brown/80">
            <SafeIcon icon={FiMapPin} className="text-warm-brown/50 mt-0.5" />
            <span>{order.customerInfo.address}</span>
          </div>
        )}
      </div>

      {/* Order Items - Collapsed View */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-warm-brown font-medium">
            {order.items.length} item{order.items.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-warm-brown hover:text-warm-brown/80 transition-colors"
          >
            <span className="text-sm">{isExpanded ? 'Hide' : 'Show'} Details</span>
            <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="text-sm" />
          </button>
        </div>

        {/* Quick item preview */}
        {!isExpanded && (
          <div className="text-sm text-warm-brown/70">
            {order.items.slice(0, 2).map((item, index) => (
              <span key={index}>
                {item.quantity}x {item.name}
                {index < Math.min(order.items.length, 2) - 1 && ', '}
              </span>
            ))}
            {order.items.length > 2 && ` +${order.items.length - 2} more`}
          </div>
        )}

        {/* Expanded Items */}
        <motion.div
          initial={false}
          animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {isExpanded && (
            <div className="space-y-3 mt-3">
              {order.items.map((item, index) => (
                <div key={index} className="bg-cream rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-warm-brown">
                        {item.quantity}x {item.name}
                      </h4>
                      
                      {/* Customizations */}
                      {item.customizations && (
                        <div className="text-xs text-warm-brown/70 mt-1 space-y-1">
                          {item.customizations.bread && (
                            <p>• Bread: {item.customizations.bread}</p>
                          )}
                          {item.customizations.protein && (
                            <p>• Protein: {item.customizations.protein}</p>
                          )}
                          {item.customizations.toppings?.length > 0 && (
                            <p>• Toppings: {item.customizations.toppings.join(', ')}</p>
                          )}
                          {item.customizations.extras?.length > 0 && (
                            <p>• Extras: {item.customizations.extras.join(', ')}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="font-bold text-warm-brown">
                      ${item.totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}

              {/* Special Instructions */}
              {order.customerInfo.specialInstructions && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <h4 className="font-medium text-orange-800 mb-1">Special Instructions:</h4>
                  <p className="text-sm text-orange-700">{order.customerInfo.specialInstructions}</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-warm-brown/10 bg-cream/30">
        <div className="flex gap-3">
          {getNextStatus(order.status) && (
            <button
              onClick={() => onStatusChange(order.id, getNextStatus(order.status))}
              className="flex-1 bg-warm-brown text-cream py-2 px-4 rounded-lg font-medium hover:bg-warm-brown/90 transition-colors"
            >
              {getStatusButtonText(order.status)}
            </button>
          )}
          
          {order.status === 'pending' && (
            <button
              onClick={() => onStatusChange(order.id, 'cancelled')}
              className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              Cancel
            </button>
          )}
          
          {order.status === 'completed' && (
            <div className="flex-1 text-center py-2 text-warm-brown/70 font-medium">
              Order Completed
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default OrderCard;